import * as React from 'react';

// PACKAGES
import { Formik, Field, ErrorMessage, Form } from 'formik';
import * as Yup from 'yup';

// COMPONENTS
import SingleSelect from '../../../components/SingleSelect';
import FileUpload from '../../../components/FileUpload';
import AddLaterRadio from '../../../components/AddLaterRadio';
import Button from '../../../components/Button';
import DateInput from '../../../components/DateInput';
import ExistingCertification from '../../../components/ExistingCertification';

// TYPES
import { CertificationTypes } from './CertificationStaff';
import { FileType } from '../../../components/FileUpload';
import { AccountExistingCertificationsCertification, DomainCertificationType } from '../../../api/models';

interface PartThreeTypes {
	certificationData: CertificationTypes;
	setFormSection: React.Dispatch<React.SetStateAction<string>>;
	setCertificationFormData: React.Dispatch<React.SetStateAction<CertificationTypes>>;
	existingCertifications: AccountExistingCertificationsCertification[];
}

interface PoliceCheckType {
	certificationType?: DomainCertificationType;
	date?: string;
	createAttachments?: string[];
	attachments?: FileType[];
	addLater?: boolean;
}

const PartThree: React.FC<PartThreeTypes> = ({
	certificationData,
	setFormSection,
	setCertificationFormData,
	existingCertifications
}) => {
	const onboardingDocs = [{ label: 'Police Check', value: 'PoliceCheck' }];

	// Set Initial Values
	const partThreeInitialValues: PoliceCheckType = {
		certificationType: certificationData.policeCheck?.certificationType,
		date: certificationData.policeCheck?.date || '',
		attachments: certificationData.policeCheck?.attachments || [],
		createAttachments: certificationData.policeCheck?.createAttachments || [],
		addLater: certificationData.policeCheck?.certificationType
			? false
			: existingCertifications?.some((item) => item.validCertification)
	};

	//Submit form
	const continueFormStepTwo = (values: PoliceCheckType) => {
		setFormSection('4');
		if (values.addLater) {
			setCertificationFormData((prevState) => ({
				...prevState,
				policeCheck: {
					certificationType: undefined,
					date: '',
					createAttachments: []
				}
			}));
		}
	};

	// Validation
	const formTwoValidationSchema = Yup.object().shape({
		certificationType: Yup.string().when('addLater', { is: false, then: Yup.string().required('Required') }),
		date: Yup.string().when('addLater', { is: false, then: Yup.string().required('Required') }),
		createAttachments: Yup.array().when('addLater', {
			is: false,
			then: Yup.array().min(1, 'Required').required('Required')
		})
	});

	return (
		<Formik
			validationSchema={formTwoValidationSchema}
			onSubmit={continueFormStepTwo}
			initialValues={partThreeInitialValues}
			enableReinitialize={true}
		>
			{(formik) => {
				return (
					<Form translate="yes">
						<div className="form-group">
							<label htmlFor="certificationType" className="label">
								3. Please provide supporting Documents
							</label>

							<Field
								name="certificationType"
								children={() => {
									return (
										<SingleSelect
											name="certificationType"
											options={onboardingDocs}
											value={formik.values.certificationType}
											placeholder="Select form of documentation..."
											onChange={(val: DomainCertificationType) => {
												formik.setFieldValue('certificationType', val);
												formik.setFieldValue('addLater', false); // pretty sure this doesn't need to be done, since Formik sorts this logic out initialValues and when the data changes thanks to 'enableReinitialize=true'
												setCertificationFormData((prevState) => ({
													...prevState,
													policeCheck: {
														...prevState.policeCheck,
														certificationType: val
													}
												}));
											}}
										/>
									);
								}}
							/>

							<ErrorMessage name={`certificationType`}>
								{(error) => <div className="form-validation-error">{error}</div>}
							</ErrorMessage>
						</div>

						{!formik.values.addLater && formik.values.certificationType ? (
							<>
								<div className="form-group">
									<label htmlFor="date">Date of police check</label>

									<DateInput
										showDropdowns={true}
										excludeFuture={true}
										dateTimeString={formik.values.date || ''}
										onChange={(val: string) => {
											formik.setFieldValue('date', val);
											setCertificationFormData((prevState) => ({
												...prevState,
												policeCheck: {
													...prevState.policeCheck,
													date: val
												}
											}));
										}}
									/>

									<ErrorMessage name={`date`}>
										{(error) => <div className="form-validation-error">{error}</div>}
									</ErrorMessage>
								</div>

								<div className="form-group">
									<Field
										children={() => {
											return (
												<>
													<FileUpload
														name={`createAttachments`}
														placeholder="Choose file..."
														onChange={(val: string, files: FileType[]) => {
															formik.setFieldValue('createAttachments', val);
															setCertificationFormData((prevState) => ({
																...prevState,
																policeCheck: {
																	...prevState.policeCheck,
																	createAttachments: [val],
																	attachments: files
																}
															}));
														}}
														multiple={false}
														files={formik.values.attachments}
														label="Photo of identification"
													/>
												</>
											);
										}}
									/>

									<ErrorMessage name={`createAttachments`}>
										{(error) => <div className="form-validation-error">{error}</div>}
									</ErrorMessage>
								</div>
							</>
						) : null}

						<AddLaterRadio
							changeToUseExisting={existingCertifications.length > 0}
							onChange={(val: boolean) => {
								formik.setFieldValue('addLater', val);
								formik.setFieldValue('certificationType', '');
								// Clear any entered police check details so leftover input is not displayed when switching back to manual entry
								if (val) {
									formik.setFieldValue('date', '');
									formik.setFieldValue('createAttachments', []);
									formik.setFieldValue('attachments', []);
									// Also reset touched state so the cleared fields do not immediately show validation errors when re-entering details
									formik.setFieldTouched('date', false);
									formik.setFieldTouched('createAttachments', false);
									// The mirrored copy must be cleared as well — enableReinitialize rebuilds this form from certificationData, so stale mirrored details would be restored when the user re-selects a document type
									setCertificationFormData((prevState) => ({
										...prevState,
										policeCheck: {
											certificationType: undefined,
											date: '',
											createAttachments: [],
											attachments: []
										}
									}));
								}
							}}
							value={formik.values.addLater}
						/>
						<div className="general-divider"></div>

						{existingCertifications.length > 0 ? (
							<ExistingCertification existingCertifications={existingCertifications} />
						) : null}

						<Button type="submit" className="btn btn-primary full-width">
							Continue
						</Button>
					</Form>
				);
			}}
		</Formik>
	);
};

export default PartThree;
